import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { difference, isNil } from 'lodash-es'
import styled from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { hasTokenPattern } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/tokens'
import { AIDraftStatus } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { AIDraftPromptType } from '@lyrahealth-inc/shared-app-logic/src/models/draft/AIDraft'
import { MessageToken } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Tokens'
import { MessengerUtilityItems } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/messengerUtilities/MessengerUtilities'
import { ChatLeftSideBar } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/chatLeftSideBar/ChatLeftSideBar'
import { ConversationFilter } from '@lyrahealth-inc/ui-core-crossplatform/src/ui-models/messages/Conversations'

import BaseMessenger from './BaseMessenger'
import MessengerUtilityItemContent from './utilityItem/MessengerUtilityItemContent'
import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { CLIENT_HOME } from '../../../common/constants/routingConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../data/store'
import { getUserLastConversationClientId } from '../../../data/userSettingsSelectors'
import { setLastConversationClientId } from '../../../data/userSettingsSlice'
import { clearLTAppointments } from '../../clients/clientDetails/data/appointmentsSlice'
import { useClientAppointmentsData } from '../../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../../clients/clientDetails/data/hooks/useSelectedClientData'
import { clearSelectedClientId, setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { ChatEntryPoint, InsertMessageOptions } from '../BaseChat'
import { useGetAIDraftsBulkQuery } from '../data/messagesApi'
import { getMessageCenterTarget } from '../data/messagesSelectors'
import { clearSelectedConversation, setMessageCenterTarget } from '../data/messagesSlice'
import { convertTemplateWithTokens } from '../data/utils'
import useChatPopout from '../hooks/useChatPopout'
import useGetFilteredConversations from '../hooks/useGetFilteredConversations'
import { useMessageInsertion } from '../hooks/useMessageInsertion'
import useNavigateToClientAssignment from '../hooks/useNavigateToClientAssignment'
import useNavigateToClientNote from '../hooks/useNavigateToClientNote'

const Container = styled.View({
  flexDirection: 'row',
  flex: 1,
})

const ChatLeftSideBarContainer = styled(ChatLeftSideBar)(({ theme }) => ({
  borderRightWidth: '1px',
  borderRightColor: theme.colors.borderDefault,
}))

const MainSection = styled.View({
  flex: 1,
  overflow: 'hidden',
})

function getEventNameFromFilter(filter: ConversationFilter) {
  switch (filter) {
    case ConversationFilter.UNREAD:
      return mixpanelEvents.MESSAGE_CENTER_USE_UNREAD_FILTER
    case ConversationFilter.ACTIVE:
      return mixpanelEvents.MESSAGE_CENTER_USE_ACTIVE_FILTER
    case ConversationFilter.UNSCHEDULED:
      return mixpanelEvents.MESSAGE_CENTER_USE_UNSCHEDULED_FILTER
    case ConversationFilter.LYRA_AI_ENABLED:
      return mixpanelEvents.MESSAGE_CENTER_USE_LYRA_AI_ENABLED_FILTER
  }
}

const Messenger: React.FC = () => {
  const {
    showTemplatesButtonInMessageCenter,
    shouldShowMessengerDraftsUtility,
    shouldShowMessengerAIDrafts,
    shouldShowPostSessionAIDrafts,
  } = useFlags()
  const [filters, setFilters] = useState<ConversationFilter[]>([])
  const navigate = useNavigate()
  const selectedClientId = useSelector(getSelectedClientId)
  const messageCenterTarget = useSelector(getMessageCenterTarget)
  const lastConversationClientId = useSelector(getUserLastConversationClientId)
  const { onOpenNewWindow } = useChatPopout(selectedClientId)
  const dispatch = useAppDispatch()
  const hasSetInitialClientId = useRef(false)

  const [isComposing, setIsComposing] = useState(false)
  const [openUtilityItem, setOpenUtilityItem] = useState<MessengerUtilityItems | null>(null)
  const [scrolledToClientId, setScrolledToClientId] = useState<string | null>(null)
  const navigateToAssignment = useNavigateToClientAssignment()
  const navigateToNote = useNavigateToClientNote()

  const [savedMessageBeforeClientSelection, setSavedMessageBeforeClientSelection] = useState<string | null>(null)
  const {
    messageToInsert,
    setMessageToInsert,
    currentTemplateId,
    shouldInsertDBL,
    shouldInsertEssentialsLink,
    insertMessage: insertMessageIntoChat,
    insertDirectBookingLink,
    insertEssentialsArticleChip,
    onDBLInserted,
    onEssentialsLinkInserted,
  } = useMessageInsertion(selectedClientId)
  const insertMessage = useCallback(
    (message: string | null, opts?: InsertMessageOptions) => {
      if (message !== null && message !== '' && MessengerUtilityItems.TEMPLATES === openUtilityItem) {
        setSavedMessageBeforeClientSelection(message)
      }
      insertMessageIntoChat(message, opts)
    },
    [insertMessageIntoChat, openUtilityItem],
  )
  const { data: appointmentsData } = useClientAppointmentsData()
  const nextClientAppointment = appointmentsData?.find(
    (appt: any) =>
      new Date(`${appt.startDate} ${appt?.startTime}`) > new Date() && appt?.appointmentStatus !== 'canceled',
  )
  const { data: clientDetails, isFetching: isClientDetailsFetching } = useSelectedClientData()

  const providerId = useSelector(getAuthUserId)
  const entryTarget =
    messageCenterTarget?.clientId === selectedClientId && messageCenterTarget.providerId === providerId
      ? messageCenterTarget
      : null

  useEffect(
    () => () => {
      dispatch(setMessageCenterTarget(null))
    },
    [dispatch],
  )

  useEffect(() => {
    if (
      messageCenterTarget &&
      ((selectedClientId && messageCenterTarget.clientId !== selectedClientId) ||
        messageCenterTarget.providerId !== providerId)
    ) {
      dispatch(setMessageCenterTarget(null))
    }
  }, [dispatch, messageCenterTarget, providerId, selectedClientId])
  const { data: aiDraftsIntake } = useGetAIDraftsBulkQuery(
    {
      promptType: AIDraftPromptType.INTAKE,
      statusOnly: false,
      providerId: providerId,
    },
    { refetchOnMountOrArgChange: true, skip: !shouldShowMessengerAIDrafts },
  )
  const { data: aiPostSessionDrafts } = useGetAIDraftsBulkQuery(
    {
      promptType: AIDraftPromptType.POST_SESSION,
      statusOnly: false,
      providerId: providerId,
    },
    { refetchOnMountOrArgChange: true, skip: !shouldShowPostSessionAIDrafts },
  )

  const totalAIDraftCount = useMemo(() => {
    const allDrafts = [...(aiDraftsIntake ?? []), ...(aiPostSessionDrafts ?? [])]
    return allDrafts.filter((draft) => draft.status === AIDraftStatus.COMPLETE).length
  }, [aiDraftsIntake, aiPostSessionDrafts])

  const { conversations: conversationsMapped, isLoading: isLoadingConversations } = useGetFilteredConversations(filters)

  const onConversationPressed = useCallback(
    ({ clientId, externallySelected }: { clientId: string; externallySelected?: boolean }) => {
      setIsComposing(false)
      setOpenUtilityItem(null)
      if (selectedClientId === clientId) {
        return
      }
      dispatch(setSelectedClientId(clientId))
      dispatch(setLastConversationClientId(clientId))

      if (externallySelected) {
        setScrolledToClientId(clientId)
      }
    },
    [selectedClientId, dispatch],
  )

  useEffect(() => {
    if (!hasSetInitialClientId.current && selectedClientId == null && lastConversationClientId) {
      dispatch(setSelectedClientId(lastConversationClientId))
      hasSetInitialClientId.current = true
    }
  }, [dispatch, lastConversationClientId, selectedClientId])

  const onTemplatePressed = useCallback(() => {
    setIsComposing(false)
    setOpenUtilityItem(MessengerUtilityItems.TEMPLATES)
    track({
      event: mixpanelEvents.OPEN_MESSAGE_TEMPLATES_FROM_MESSAGE_CENTER,
    })
    dispatch(setMessageCenterTarget(null))
    dispatch(clearSelectedClientId())
  }, [dispatch, setOpenUtilityItem])

  const onOpenClientProfile = useCallback(() => {
    navigate(CLIENT_HOME.route)
  }, [navigate])

  // Also used by the templates utility after it inserts a template, so it must keep the pending insertion.
  const startCompose = useCallback(() => {
    track({
      event: mixpanelEvents.MESSAGE_CENTER_COMPOSE,
    })
    setSelectedClientId(null)
    dispatch(setLastConversationClientId(undefined))
    dispatch(clearLTAppointments())
    setIsComposing(true)
    setOpenUtilityItem(null)
    dispatch(setMessageCenterTarget(null))
    dispatch(clearSelectedClientId())
  }, [dispatch, setOpenUtilityItem])

  const onComposePressed = useCallback(() => {
    // A template inserted for the previous client must not be tagged onto the next client's message.
    insertMessageIntoChat(null)
    startCompose()
  }, [insertMessageIntoChat, startCompose])

  const onFiltersChanged = useCallback((filters: ConversationFilter[]) => {
    setFilters((prev) => {
      const newFilters = difference(filters, prev)
      const removedFilters = difference(prev, filters)
      const filtersChangedMap = Object.fromEntries(
        Object.values(ConversationFilter).map((filter) => {
          return [filter, newFilters.includes(filter) ? true : removedFilters.includes(filter) ? false : null]
        }),
      )
      Object.entries(filtersChangedMap).forEach(([filter, value]: [ConversationFilter, boolean | null]) => {
        if (value != null) {
          const eventName = getEventNameFromFilter(filter)
          track({
            event: eventName,
            properties: { selected: value },
          })
        }
      })
      return filters
    })
  }, [])

  const onDraftsPressed = useCallback(() => {
    dispatch(clearSelectedConversation())
    dispatch(setMessageCenterTarget(null))
    dispatch(clearSelectedClientId())
    setOpenUtilityItem(MessengerUtilityItems.DRAFTS)
    track({
      event: mixpanelEvents.OPEN_MESSAGE_DRAFTS_FROM_MESSAGE_CENTER,
    })
  }, [dispatch, setOpenUtilityItem])

  const onSmartSuggestionsPressed = useCallback(() => {
    dispatch(setMessageCenterTarget(null))
    dispatch(clearSelectedClientId())
    setOpenUtilityItem(MessengerUtilityItems.SMART_SUGGESTIONS)
    track({
      event: mixpanelEvents.OPEN_SMART_SUGGESTIONS_FROM_MESSAGE_CENTER,
    })
  }, [dispatch, setOpenUtilityItem])

  const onOpenAssignment = useCallback(
    (assignmentId: string) => {
      if (!selectedClientId) {
        return
      }
      navigateToAssignment(selectedClientId, assignmentId)
    },
    [navigateToAssignment, selectedClientId],
  )

  const onOpenNotes = useCallback(
    (noteId: string) => {
      if (!selectedClientId) {
        return
      }
      navigateToNote(selectedClientId, noteId)
    },
    [navigateToNote, selectedClientId],
  )

  useEffect(() => {
    if (selectedClientId && savedMessageBeforeClientSelection && clientDetails && !isClientDetailsFetching) {
      let messageToInsert = savedMessageBeforeClientSelection
      if (hasTokenPattern(savedMessageBeforeClientSelection)) {
        messageToInsert = convertTemplateWithTokens(
          savedMessageBeforeClientSelection,
          clientDetails,
          nextClientAppointment,
        )
      }
      if (
        savedMessageBeforeClientSelection.includes(`{{token:${MessageToken.BOOKING_LINK}}}`) ||
        savedMessageBeforeClientSelection.includes('<token name="booking_link" label="Booking Link">')
      ) {
        insertDirectBookingLink()
      }
      insertMessage(messageToInsert, {
        attributesToAttach: {
          message_template_id: currentTemplateId,
        },
      })
      setSavedMessageBeforeClientSelection(null)
    }
  }, [
    selectedClientId,
    clientDetails,
    nextClientAppointment,
    savedMessageBeforeClientSelection,
    isClientDetailsFetching,
    insertDirectBookingLink,
    insertMessage,
    currentTemplateId,
  ])

  return (
    <Container>
      <ChatLeftSideBarContainer
        isLoading={isLoadingConversations}
        conversations={conversationsMapped}
        onComposePressed={onComposePressed}
        onConversationPressed={onConversationPressed}
        onTemplatePressed={showTemplatesButtonInMessageCenter ? onTemplatePressed : undefined}
        onDraftsPressed={shouldShowMessengerDraftsUtility ? onDraftsPressed : undefined}
        onSmartSuggestionPress={
          shouldShowMessengerAIDrafts || shouldShowPostSessionAIDrafts ? onSmartSuggestionsPressed : undefined
        }
        smartSuggestionCounter={totalAIDraftCount}
        openUtilityItem={openUtilityItem}
        selectedClientId={selectedClientId}
        scrolledToClientId={scrolledToClientId}
        setScrolledToClientId={setScrolledToClientId}
        setOpenUtilityItem={setOpenUtilityItem}
        filters={filters}
        onFiltersChanged={onFiltersChanged}
      />
      <MainSection>
        {!openUtilityItem ? (
          <BaseMessenger
            initialThreadId={entryTarget?.threadId}
            initialMessage={entryTarget?.message}
            onInitialMessageInserted={() => {
              if (entryTarget?.message) dispatch(setMessageCenterTarget({ ...entryTarget, message: '' }))
            }}
            initialProviderPairCounterpart={entryTarget?.draftCounterpart}
            selectedClientId={selectedClientId}
            isComposing={isComposing}
            onSelectedClientIdChanged={(clientId) => {
              dispatch(setSelectedClientId(clientId))
              dispatch(setLastConversationClientId(clientId ?? undefined))
            }}
            showEmptyView={selectedClientId == null}
            onIsComposingChanged={setIsComposing}
            onOpenNewWindow={isNil(openUtilityItem) ? onOpenNewWindow : undefined}
            onOpenClientProfile={onOpenClientProfile}
            onOpenAssignment={onOpenAssignment}
            onOpenNotes={onOpenNotes}
            initialDetailPanelOpen={true}
            entryPoint={ChatEntryPoint.MESSAGES_TAB}
            messageToInsert={messageToInsert}
            insertMessage={insertMessage}
            isLoadingClient={isClientDetailsFetching}
            shouldInsertDBL={shouldInsertDBL}
            onDBLInserted={onDBLInserted}
            insertDirectBookingLink={insertDirectBookingLink}
            shouldInsertEssentialsLink={shouldInsertEssentialsLink}
            onEssentialsLinkInserted={onEssentialsLinkInserted}
            insertEssentialsArticleChip={insertEssentialsArticleChip}
            setMessageToInsert={setMessageToInsert}
            currentTemplateId={currentTemplateId}
          />
        ) : (
          <MessengerUtilityItemContent
            selectedItem={openUtilityItem}
            onConversationPressed={onConversationPressed}
            onSendPressed={startCompose}
            insertMessage={insertMessage}
          />
        )}
      </MainSection>
    </Container>
  )
}

export default Messenger
